import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  AffiliateUsersQuery,
  AffiliateUsersResponseData,
  ApiSuccessEnvelope,
} from "../../types/affiliates";
import {
  getAnalyticsInstance,
  type AffiliateAnalyticsDataSource,
} from "./analyticsDataSource";

const DEFAULT_LIMIT = 25;

const usersPath = (
  dataSource: AffiliateAnalyticsDataSource,
  affiliateId: string,
) =>
  // Portal route carries no affiliate_id — the token scopes the query.
  dataSource === "portal"
    ? API_ROUTES.AFFILIATE_PORTAL_ANALYTICS_USERS
    : API_ROUTES.AFFILIATE_DETAIL_ANALYTICS_USERS.replace(
        "{affiliate_id}",
        encodeURIComponent(affiliateId),
      );

// FE keeps a "yes"/"no" enum for the FTD select (matches the UI copy).
// BE takes a plain boolean, so translate at the boundary.
const mapFtdParam = (value: AffiliateUsersQuery["ftd"]): string | undefined => {
  if (value === "yes") return "true";
  if (value === "no") return "false";
  return undefined;
};

// The `search` string is routed to whichever field the UI picked in the
// search-by dropdown: username | user_id | email. The BE accepts each as
// its own top-level param.
const buildSearchParam = (
  query: AffiliateUsersQuery,
): Record<string, string> => {
  const term = query.search?.trim();
  if (!term || !query.search_field) return {};
  if (query.search_field === "username") return { username: term };
  if (query.search_field === "user_id") return { user_id: term };
  if (query.search_field === "email") return { email: term };
  return {};
};

export const fetchAffiliateUsers = async (
  affiliateId: string,
  query: AffiliateUsersQuery = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
): Promise<AffiliateUsersResponseData> => {
  const ftdParam = mapFtdParam(query.ftd);
  const response = await getAnalyticsInstance(dataSource).get<
    ApiSuccessEnvelope<AffiliateUsersResponseData>
  >(usersPath(dataSource, affiliateId), {
    params: {
      limit: query.limit ?? DEFAULT_LIMIT,
      ...(query.cursor ? { cursor: query.cursor } : {}),
      ...(query.sort_by ? { sort_by: query.sort_by } : {}),
      ...(query.sort_desc === undefined ? {} : { sort_desc: query.sort_desc }),
      // BE enforces both-or-neither on the range.
      ...(query.start_date && query.end_date
        ? { start_date: query.start_date, end_date: query.end_date }
        : {}),
      ...buildSearchParam(query),
      ...(query.promo_code ? { promo_code: query.promo_code } : {}),
      ...(ftdParam !== undefined ? { ftd: ftdParam } : {}),
      ...(query.registration_state
        ? { registration_state: query.registration_state }
        : {}),
    },
  });
  return response.data.data;
};

export const useAffiliateUsers = (
  affiliateId: string | undefined,
  query: AffiliateUsersQuery = {},
  dataSource: AffiliateAnalyticsDataSource = "operator",
): UseQueryResult<AffiliateUsersResponseData, Error> =>
  useQuery<AffiliateUsersResponseData, Error>({
    // Every param that reaches the wire also keys the cache — including the
    // search + filter fields
    queryKey: [
      "affiliates",
      "users",
      dataSource,
      affiliateId ?? "",
      query.limit ?? DEFAULT_LIMIT,
      query.cursor ?? "",
      query.sort_by ?? "",
      query.sort_desc ?? "",
      query.start_date ?? "",
      query.end_date ?? "",
      query.search_field ?? "",
      query.search ?? "",
      query.promo_code ?? "",
      query.ftd ?? "",
      query.registration_state ?? "",
    ],
    queryFn: () => fetchAffiliateUsers(affiliateId ?? "", query, dataSource),
    enabled: dataSource === "portal" || !!affiliateId,
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
  });
